<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { MenuItem } from '@/types/menu';

interface Props {
  item: MenuItem;
  variant?: 'mobile' | 'desktop';
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'desktop'
});

const { t } = useI18n();

function hasDiscount(price?: number): boolean {
  return Boolean(price && price > 0);
}

function resolvePrice(price: number, discountPrice?: number): string {
  return (hasDiscount(discountPrice) ? discountPrice! : price).toFixed(2);
}
</script>

<template>
  <div :class="variant === 'mobile' ? 'mb-2 space-y-0.5' : 'space-y-1'">
    <template v-if="item.variants && item.variants.length > 0">
      <div
        v-if="item.price"
        class="flex justify-between items-center"
        :class="variant === 'mobile' ? 'text-sm' : 'gap-2'"
      >
        <span class="text-gray-600 dark:text-gray-400">Base:</span>
        <div class="flex items-center gap-2">
          <span
            v-if="hasDiscount(item.discount_price)"
            class="text-gray-500 dark:text-gray-400 line-through text-xs"
          >
            ${{ item.price.toFixed(2) }}
          </span>
          <span
            class="font-medium"
            :class="hasDiscount(item.discount_price)
              ? 'text-green-600 dark:text-green-400'
              : (variant === 'mobile' ? 'text-gray-900 dark:text-white font-semibold' : '')"
          >
            ${{ resolvePrice(item.price, item.discount_price) }}
          </span>
          <span
            v-if="hasDiscount(item.discount_price)"
            class="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-200"
          >
            {{ t('app.forms.sale_badge') }}
          </span>
        </div>
      </div>

      <div
        v-for="variantItem in item.variants"
        :key="variantItem.id"
        class="flex justify-between items-center"
        :class="variant === 'mobile' ? 'text-sm' : 'gap-2'"
      >
        <span class="text-gray-600 dark:text-gray-400">{{ variantItem.name }}:</span>
        <div class="flex items-center gap-2">
          <span
            v-if="hasDiscount(variantItem.discount_price)"
            class="text-gray-500 dark:text-gray-400 line-through text-xs"
          >
            ${{ variantItem.price.toFixed(2) }}
          </span>
          <span
            class="font-medium"
            :class="hasDiscount(variantItem.discount_price)
              ? 'text-green-600 dark:text-green-400'
              : (variant === 'mobile' ? 'text-gray-900 dark:text-white font-semibold' : '')"
          >
            ${{ resolvePrice(variantItem.price, variantItem.discount_price) }}
          </span>
          <span
            v-if="hasDiscount(variantItem.discount_price)"
            class="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-200"
          >
            {{ t('app.forms.sale_badge') }}
          </span>
        </div>
      </div>
    </template>

    <template v-else>
      <div
        class="flex items-center justify-between gap-2"
        :class="variant === 'mobile' ? '' : 'justify-start'"
      >
        <span
          v-if="variant === 'mobile'"
          class="text-sm text-gray-600 dark:text-gray-400"
        >
          {{ t('app.views.menu.list.table.price') }}:
        </span>
        <span
          v-if="hasDiscount(item.discount_price)"
          class="text-gray-500 dark:text-gray-400 line-through"
          :class="variant === 'mobile' ? 'text-sm' : ''"
        >
          ${{ item.price?.toFixed(2) || '0.00' }}
        </span>
        <span
          :class="[
            hasDiscount(item.discount_price) ? 'text-green-600 dark:text-green-400' : (variant === 'mobile' ? 'text-gray-900 dark:text-white' : ''),
            variant === 'mobile' ? 'text-lg font-semibold' : 'font-medium'
          ]"
        >
          ${{ hasDiscount(item.discount_price) ? item.discount_price?.toFixed(2) : item.price?.toFixed(2) || '0.00' }}
        </span>
        <span
          v-if="hasDiscount(item.discount_price)"
          class="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-200"
        >
          {{ t('app.forms.sale_badge') }}
        </span>
      </div>
    </template>
  </div>
</template>
